import type { Permission, ViewKey } from '../access/role';
import {
  columnChoices,
  layoutOf,
  shownColumns,
  type LayoutColumn,
  type TableLayout,
} from '../table/tableLayout';

/**
 * The widgets of the two screens a person arranges for themselves: the
 * Dashboard, and Management's Overview. The lab's request of 12 October 2026:
 * "a dashboard, which is customizable", where people "add different items,
 * change their place and customize their dashboards with widgets related to
 * their work", and the Overview "stays this by default, but enable
 * customization".
 *
 * A widget is a piece of a screen the person already holds, drawn small: the
 * cases due today are Orders narrowed, what the lab is owed is Payments'
 * figures. So every widget names what its reader must hold, in the terms the
 * server answers its request by, and a widget is offered only to somebody the
 * server would answer. Nothing here grants anything. What a person may read is
 * the access matrix's and their own record's, as everywhere else: give
 * somebody Orders and the case widgets are theirs to add, take it away and
 * they are gone from the dashboard without anybody touching it.
 *
 * How a person arranged a screen is kept on their account the way a table's
 * columns are, under the screen's own key: the widgets in their order, and the
 * ones left out (`TableLayout`). So a widget added in a release takes the place
 * it was designed for, one the person may no longer read is simply not drawn,
 * and Reset to default is the screen as it comes.
 */

/** The screens made of widgets. Both are menu screens, granted in the matrix like any other. */
export const WIDGET_SCREENS = ['overview', 'dashboard'] as const;
export type WidgetScreen = (typeof WIDGET_SCREENS)[number];

export function isWidgetScreen(view: ViewKey | null | undefined): view is WidgetScreen {
  return view === 'overview' || view === 'dashboard';
}

/**
 * Every widget there is, in the order the list of them reads: the Overview's
 * own sections first, as that screen has always drawn them, then the ones for
 * everybody, cases, the dispatch desk and money.
 */
export const WIDGET_KEYS = [
  'line',
  'notifications',
  'figures',
  'sales',
  'liveWork',
  'attention',
  'team',
  'shortcuts',
  'caseCounts',
  'dueToday',
  'overdue',
  'urgent',
  'latestCases',
  'readyToShip',
  'onHold',
  'moneyOwed',
  'toBePaid',
  'partPaid',
  'changedCases',
  'released',
  'inCeramic',
  'owedByDoctor',
] as const;
export type WidgetKey = (typeof WIDGET_KEYS)[number];

export function isWidgetKey(value: string): value is WidgetKey {
  return (WIDGET_KEYS as readonly string[]).includes(value);
}

/**
 * How much of a row a widget takes on the Dashboard, where widgets flow side
 * by side: all of it, half, or a third. A row is always filled, so a widget
 * alone on its row is as wide as the row. The Overview reads top to bottom,
 * one widget under another, whatever a widget says here.
 */
export type WidgetSize = 'full' | 'half' | 'third';

/** What somebody holds, as their session lists it. */
export interface WidgetReader {
  views: readonly ViewKey[];
  permissions: readonly Permission[];
}

const holds = (reader: WidgetReader, ...views: ViewKey[]) =>
  views.some((view) => reader.views.includes(view));
const may = (reader: WidgetReader, ...permissions: Permission[]) =>
  permissions.some((permission) => reader.permissions.includes(permission));
/** Money is sent to a holder of pay or admin, and to nobody else. The API's Caller.SeesMoney. */
const seesMoney = (reader: WidgetReader) => may(reader, 'pay', 'admin');
/** The floor's board answers a holder of the Production line, of the Overview, or of admin. */
const readsBoard = (reader: WidgetReader) => holds(reader, 'line', 'overview') || may(reader, 'admin');

interface WidgetRule {
  size: WidgetSize;
  /** Counts over the reporting range chosen at the top of the screen: today, the week, the month. */
  ranged?: boolean;
  /** Whether the server answers this reader what the widget asks for. */
  open: (reader: WidgetReader) => boolean;
}

const WIDGETS: Record<WidgetKey, WidgetRule> = {
  // ---- the Overview's own sections
  line: { size: 'full', open: readsBoard },
  // A person's own notifications, which everybody has.
  notifications: { size: 'half', open: () => true },
  // The summary answers a holder of the Overview or of Reports.
  figures: { size: 'full', ranged: true, open: (reader) => holds(reader, 'overview', 'reports') },
  // The sales report is the Reports screen's, and it is money.
  sales: { size: 'half', ranged: true, open: (reader) => holds(reader, 'reports') && seesMoney(reader) },
  liveWork: { size: 'full', open: readsBoard },
  attention: { size: 'half', open: readsBoard },
  // Everybody's figures are the Staff productivity screen's.
  team: { size: 'full', ranged: true, open: (reader) => holds(reader, 'staff') },

  // ---- for everybody: the person's own screens, one press away
  shortcuts: { size: 'full', open: () => true },

  // ---- cases, which are the Orders screen's
  caseCounts: { size: 'full', open: (reader) => holds(reader, 'orders') },
  dueToday: { size: 'half', open: (reader) => holds(reader, 'orders') },
  overdue: { size: 'half', open: (reader) => holds(reader, 'orders') },
  urgent: { size: 'half', open: (reader) => holds(reader, 'orders') },
  latestCases: { size: 'half', open: (reader) => holds(reader, 'orders') },

  // ---- the dispatch desk, and the cases the lab has stopped
  readyToShip: { size: 'half', open: (reader) => holds(reader, 'shipping') },
  onHold: { size: 'half', open: (reader) => holds(reader, 'onhold') },

  // ---- money, which is the Payments screen's. Its overview asks for pay itself.
  moneyOwed: { size: 'full', open: (reader) => holds(reader, 'payments') && may(reader, 'pay') },
  toBePaid: { size: 'half', open: (reader) => holds(reader, 'payments') && seesMoney(reader) },
  partPaid: { size: 'half', open: (reader) => holds(reader, 'payments') && seesMoney(reader) },
  changedCases: { size: 'half', open: (reader) => holds(reader, 'payments') && seesMoney(reader) },
  // The lab's request of 17 October 2026: the cases released to ship unpaid (item 8), and the cases at Ceramic (item 14).
  released: { size: 'half', open: (reader) => holds(reader, 'payments') && seesMoney(reader) },
  inCeramic: { size: 'half', open: (reader) => holds(reader, 'payments') && seesMoney(reader) },
  owedByDoctor: { size: 'half', open: (reader) => holds(reader, 'payments') && may(reader, 'pay') },
};

export function widgetSize(key: WidgetKey): WidgetSize {
  return WIDGETS[key].size;
}

/** Whether a widget counts over the reporting range, so the screen offers the range while it is shown. */
export function widgetIsRanged(key: WidgetKey): boolean {
  return WIDGETS[key].ranged === true;
}

/** Whether this reader may have the widget at all: whether the server answers them what it asks for. */
export function mayShowWidget(key: WidgetKey, reader: WidgetReader): boolean {
  return WIDGETS[key].open(reader);
}

/**
 * What each screen shows to somebody who never arranged it.
 *
 * The Overview: the sections it has always had, in the order it has always
 * drawn them. That is the whole of "stays this by default".
 *
 * The Dashboard: one list for everybody, and what a person holds decides how
 * much of it is theirs. No role is named: a receptionist's is the cases and
 * the dispatch desk because Reception holds Orders, Shipping and On Hold, and
 * an accountant's is the money because Accounts holds Payments. Give either
 * another screen and its widgets are on their dashboard the same day.
 *
 * It opens on the work: the figures first, then the notifications and the
 * lists. The person's own screens come last. They are already in the menu, and
 * on a phone seven of them are four rows that would stand before everything
 * the person opened the screen to read.
 */
const AS_IT_COMES: Record<WidgetScreen, readonly WidgetKey[]> = {
  overview: ['line', 'notifications', 'figures', 'sales', 'liveWork', 'attention', 'team'],
  dashboard: [
    'caseCounts',
    'moneyOwed',
    'notifications',
    'dueToday',
    'overdue',
    'readyToShip',
    'onHold',
    'toBePaid',
    'changedCases',
    // For the accountant to be displayed: the lab's request of 17 October 2026, item 14.
    'inCeramic',
    'owedByDoctor',
    'shortcuts',
  ],
};

/** One widget of a screen's list: a column of a layout, whose key is a widget's. */
export interface WidgetChoice extends LayoutColumn {
  key: WidgetKey;
}

/**
 * The widgets of a screen for one reader, as the list in Customize has them:
 * the ones the screen comes with, in its order, and after them every other
 * widget the reader may have, offered and left out until it is ticked. A
 * widget the reader may not have is not in the list at all.
 */
export function widgetChoices(screen: WidgetScreen, reader: WidgetReader): WidgetChoice[] {
  const comesWith = AS_IT_COMES[screen];
  const open = (key: WidgetKey) => mayShowWidget(key, reader);
  return [
    ...comesWith.filter(open).map((key) => ({ key })),
    ...WIDGET_KEYS.filter((key) => !comesWith.includes(key) && open(key)).map((key) => ({
      key,
      optional: true,
    })),
  ];
}

/** The widgets to draw, in the reader's order: what they arranged, or the screen as it comes. */
export function shownWidgets(
  screen: WidgetScreen,
  reader: WidgetReader,
  layout: TableLayout | null | undefined,
): WidgetKey[] {
  return shownColumns(widgetChoices(screen, reader), layout).filter(isWidgetKey);
}

/**
 * The key a screen's arrangement is kept under on the person's account, beside
 * their tables': `widgets.dashboard`, `widgets.overview`.
 */
export function widgetLayoutKey(screen: WidgetScreen): string {
  return `widgets.${screen}`;
}

/**
 * The home screen of somebody whose home is not made of widgets, a technician
 * at their queue among them. It carries one thing over the screen itself, the
 * person's latest notifications, and that one thing can be taken off and put
 * back like a widget: the lab's answer of 12 October 2026, "make the
 * notification on the home screen stay, but also removed". Kept the same way,
 * under its own key.
 */
export const HOME_LAYOUT_KEY = 'widgets.home';

/** The one thing a plain home screen carries. */
export const HOME_WIDGETS: readonly LayoutColumn[] = [{ key: 'notifications' }];

/**
 * The admin's row of figures over the Production line and the Orders list:
 * active, urgent, overdue and on hold. The lab's request of 17 October 2026,
 * item 5. Shown to a holder of admin until they take it off, and put back the
 * same way, kept on their account under the screen's own key like a widget.
 */
export const ADMIN_FIGURES: readonly LayoutColumn[] = [{ key: 'figures' }];

export function adminFiguresKey(screen: 'orders' | 'line'): string {
  return `figures.${screen}`;
}

/**
 * The shipping desk's figures over its tabs, kept on the account under this key
 * in the same one-column layout as the admin's: on the screen until the reader
 * takes them off. The lab's request of 22 October 2026.
 */
export const SHIPPING_FIGURES_KEY = 'figures.shipping';

/** Whether the admin's figures are on the screen: yes, until they take them off. */
export function showsAdminFigures(layout: TableLayout | null | undefined): boolean {
  return shownColumns(ADMIN_FIGURES, layout).includes('figures');
}

/** Whether a person's latest notifications are on their plain home screen: yes, until they take them off. */
export function showsHomeNotifications(layout: TableLayout | null | undefined): boolean {
  return shownColumns(HOME_WIDGETS, layout).includes('notifications');
}

/**
 * An arrangement with one widget put on or taken off, and everything else as
 * it was: what a widget's own Hide writes, and the switch in a person's
 * notification settings, whichever screen their home is.
 */
export function withWidget(
  choices: readonly LayoutColumn[],
  layout: TableLayout | null | undefined,
  key: string,
  shown: boolean,
): TableLayout {
  return layoutOf(
    columnChoices(choices, layout).map((choice) => (choice.key === key ? { ...choice, shown } : choice)),
    layout?.pageSize ?? null,
  );
}
